import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FilterList, Payments, ReceiptLong } from "@mui/icons-material";
import { useMemo, useState } from "react";
import dayjs from "dayjs";

import usePlatformUsageFees from "../../services/usePlatformUsageFees";
import {
  IPlatformUsageFee,
  IPlatformUsageFeeFilters,
  PlatformUsageFeePaymentStatus,
} from "../../types/platformUsageFee";

const STATUS_LABELS: Record<PlatformUsageFeePaymentStatus, string> = {
  pending: "Pendente",
  paid: "Pago",
  waived: "Dispensado",
};

const STATUS_COLORS: Record<
  PlatformUsageFeePaymentStatus,
  "warning" | "success" | "default"
> = {
  pending: "warning",
  paid: "success",
  waived: "default",
};

const formatCurrency = (value: string | number) => {
  return Number(value).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const sumCurrency = (
  rows: IPlatformUsageFee[],
  field: "gross_without_delivery" | "usage_fee",
) => {
  return rows.reduce((total, row) => total + Number(row[field]), 0);
};

const PlatformUsageFeesPage = () => {
  const [filters, setFilters] = useState<IPlatformUsageFeeFilters>({});
  const { data = [], isLoading, error } = usePlatformUsageFees(filters);

  const totals = useMemo(
    () => ({
      gross: sumCurrency(data, "gross_without_delivery"),
      fee: sumCurrency(data, "usage_fee"),
      pendingFee: data
        .filter((row) => row.payment_status === "pending")
        .reduce((total, row) => total + Number(row.usage_fee), 0),
    }),
    [data],
  );

  const updateFilter = (next: Partial<IPlatformUsageFeeFilters>) => {
    setFilters((current) => ({ ...current, ...next }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Payments sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography component="h1" variant="h5" fontWeight="bold">
            Taxas Multifeiras
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Bruto sem frete
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(totals.gross)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Taxa calculada
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(totals.fee)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Pendente
            </Typography>
            <Typography variant="h5" fontWeight={700} color="warning.main">
              {formatCurrency(totals.pendingFee)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <FilterList color="primary" />
          <TextField
            label="Data inicial"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.start_date || ""}
            onChange={(event) =>
              updateFilter({ start_date: event.target.value })
            }
          />
          <TextField
            label="Data final"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.end_date || ""}
            onChange={(event) => updateFilter({ end_date: event.target.value })}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.payment_status || ""}
              onChange={(event) =>
                updateFilter({
                  payment_status: event.target
                    .value as IPlatformUsageFeeFilters["payment_status"],
                })
              }
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="pending">Pendente</MenuItem>
              <MenuItem value="paid">Pago</MenuItem>
              <MenuItem value="waived">Dispensado</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Limite"
            type="number"
            size="small"
            inputProps={{ min: 1 }}
            sx={{ width: 120 }}
            value={filters.count || ""}
            onChange={(event) => updateFilter({ count: event.target.value })}
          />
          <Button variant="text" onClick={() => setFilters({})}>
            Limpar
          </Button>
        </Box>
      </Paper>

      {error ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="error">Falha ao carregar taxas.</Typography>
        </Paper>
      ) : isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Paper
          sx={{
            textAlign: "center",
            p: 6,
            border: "2px dashed",
            borderColor: "divider",
          }}
        >
          <ReceiptLong sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhuma taxa encontrada
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Feira</TableCell>
                <TableCell>Entrega</TableCell>
                <TableCell align="right">Pedidos</TableCell>
                <TableCell align="right">Bruto sem frete</TableCell>
                <TableCell align="right">Taxa 5%</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell>Observação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.fair_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      #{row.sequence}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {dayjs(row.delivery_at).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell align="right">{row.order_count}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(row.gross_without_delivery)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}>
                      {formatCurrency(row.usage_fee)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[row.payment_status]}
                      color={STATUS_COLORS[row.payment_status]}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {row.paid_at
                      ? dayjs(row.paid_at).format("DD/MM/YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 240,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.note || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PlatformUsageFeesPage;
